import { useMemo, type CSSProperties } from "react";

import "./Fireflies.css";

type CSSVariableStyle = CSSProperties & {
  [property: `--${string}`]: string | number | undefined;
};

interface Particle {
  id: string;
  size: number;
  startX: number;
  midX1: number;
  midX2: number;
  endX: number;
  duration: number;
  delay: number;
  pulseDuration: number;
  pulseDelay: number;
  peakOpacity: number;
}

export interface FirefliesProps {
  /** Additional class name applied to the component root. */
  className?: string;
  /** Number of fireflies. Values are clamped between 1 and 150. */
  count?: number;
  /** Particle and glow size multiplier. Values are clamped between 0.2 and 5. */
  scale?: number;
  /** Animation speed multiplier. Values are clamped between 0.2 and 5. */
  speed?: number;
  /** Creates a repeatable particle arrangement. */
  seed?: string | number;
  /** Renders an optional dark radial background. Transparent by default. */
  background?: boolean;
  /** Additional inline styles applied to the component root. */
  style?: CSSProperties;
}

function hashSeed(value: string | number): number {
  const text = String(value);
  let hash = 2166136261;

  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function makeRandom(seed: string | number): () => number {
  let state = hashSeed(seed) || 1;

  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function clampNumber(
  value: number,
  fallback: number,
  minimum: number,
  maximum: number,
): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(maximum, Math.max(minimum, value));
}

/**
 * Container-sized fireflies with no viewport-relative positioning.
 *
 * The parent must have a real width and height. Fireflies fills that area.
 */
export default function Fireflies({
  className = "",
  count = 64,
  scale = 1,
  speed = 1,
  seed = "fireflies",
  background = false,
  style,
}: FirefliesProps) {
  const particleCount = Math.round(clampNumber(count, 64, 1, 150));
  const safeScale = clampNumber(scale, 1, 0.2, 5);
  const safeSpeed = clampNumber(speed, 1, 0.2, 5);

  const particles = useMemo<Particle[]>(() => {
    const random = makeRandom(`${seed}:${particleCount}`);

    return Array.from({ length: particleCount }, (_, index) => {
      const startX = 7 + random() * 86;
      const midX1 = Math.min(95, Math.max(5, startX + (random() - 0.5) * 48));
      const midX2 = Math.min(95, Math.max(5, midX1 + (random() - 0.5) * 56));
      const endX = Math.min(95, Math.max(5, midX2 + (random() - 0.5) * 42));

      return {
        id: `${seed}-${index}`,
        size: 2 + random() * 8,
        startX,
        midX1,
        midX2,
        endX,
        duration: 4.5 + random() * 4.5,
        delay: -(random() * 12),
        pulseDuration: 1.5 + random() * 2.5,
        pulseDelay: -(random() * 4),
        peakOpacity: 0.48 + random() * 0.52,
      };
    });
  }, [particleCount, seed]);

  const rootClassName = [
    "fireflies",
    background ? "fireflies--background" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const rootStyle: CSSVariableStyle = {
    "--firefly-safe-edge": `${14 * safeScale}px`,
    "--firefly-glow-blur": `${2 * safeScale}px`,
    "--firefly-glow-blur-wide": `${4 * safeScale}px`,
    ...style,
  };

  return (
    <div aria-hidden="true" className={rootClassName} style={rootStyle}>
      <div className="fireflies__field">
        {particles.map((particle) => {
          const particleStyle: CSSVariableStyle = {
            "--size": `${particle.size * safeScale}px`,
            "--start-x": `${particle.startX}%`,
            "--mid-x-1": `${particle.midX1}%`,
            "--mid-x-2": `${particle.midX2}%`,
            "--end-x": `${particle.endX}%`,
            "--travel-duration": `${particle.duration / safeSpeed}s`,
            "--travel-delay": `${particle.delay}s`,
            "--pulse-duration": `${particle.pulseDuration}s`,
            "--pulse-delay": `${particle.pulseDelay}s`,
            "--peak-opacity": particle.peakOpacity,
          };

          return (
            <span
              className="fireflies__particle"
              key={particle.id}
              style={particleStyle}
            >
              <span className="fireflies__glow" />
            </span>
          );
        })}
      </div>
    </div>
  );
}
